import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';

export type DemoModeState = {
  enabled: boolean;
  datasetId: string | null;
  enabledAt: string | null;
};

type DemoTradesOverride = {
  enabled: true;
  trades: Record<string, any>[];
} | null;

const DEFAULT_STATE: DemoModeState = {
  enabled: false,
  datasetId: null,
  enabledAt: null,
};

let cachedSnapshot: DemoModeState = DEFAULT_STATE;
let cachedRawSnapshot: string | null = null;

export const DASHBOARD_DEMO_DATASET_ID = 'prospect-dashboard-v1';
export const DASHBOARD_DEMO_MODE_STORAGE_KEY = 'tradeproof.dashboard-demo-mode.v1';

const DASHBOARD_DEMO_MODE_CHANGED_EVENT = 'tradeproof:dashboard-demo-mode-changed';
const DemoTradesOverrideContext = createContext<DemoTradesOverride>(null);

function sanitizeDemoModeState(value: unknown): DemoModeState {
  if (!value || typeof value !== 'object') return DEFAULT_STATE;

  const parsed = value as Record<string, unknown>;
  if (!parsed.enabled) return DEFAULT_STATE;

  return {
    enabled: true,
    datasetId: typeof parsed.datasetId === 'string' && parsed.datasetId.trim()
      ? parsed.datasetId.trim()
      : DASHBOARD_DEMO_DATASET_ID,
    enabledAt: typeof parsed.enabledAt === 'string' && parsed.enabledAt.trim()
      ? parsed.enabledAt.trim()
      : null,
  };
}

export const PUBLIC_GUEST_SESSION_STORAGE_KEY = 'tradeproof.public-guest-session.v1';
export const PUBLIC_GUEST_SESSION_CHANGED_EVENT = 'tradeproof:public-guest-session-changed';
export const GUEST_RESTRICTED_ACTION_EVENT = 'tradeproof:guest-restricted-action';

export function triggerGuestRestrictedAction(featureName?: string) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(GUEST_RESTRICTED_ACTION_EVENT, { detail: { featureName } }));
}

export type AppMode = 'authenticated' | 'guest';

export type GuestPermissions = {
  canViewDemoData: boolean;
  canUseFilters: boolean;
  canNavigate: boolean;
  canImportTrades: boolean;
  canConnectBroker: boolean;
  canPersistChanges: boolean;
  canAccessBilling: boolean;
  canAccessAccountSettings: boolean;
};

export const GUEST_MODE_PERMISSIONS: GuestPermissions = {
  canViewDemoData: true,
  canUseFilters: true,
  canNavigate: true,
  canImportTrades: false,
  canConnectBroker: false,
  canPersistChanges: false,
  canAccessBilling: false,
  canAccessAccountSettings: false,
};

export const AUTHENTICATED_PERMISSIONS: GuestPermissions = {
  canViewDemoData: true,
  canUseFilters: true,
  canNavigate: true,
  canImportTrades: true,
  canConnectBroker: true,
  canPersistChanges: true,
  canAccessBilling: true,
  canAccessAccountSettings: true,
};

export function readPublicGuestSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(PUBLIC_GUEST_SESSION_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function emitPublicGuestSessionChange() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PUBLIC_GUEST_SESSION_CHANGED_EVENT));
}

export function enablePublicGuestSession() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(PUBLIC_GUEST_SESSION_STORAGE_KEY, 'true');
  } catch {}
  emitPublicGuestSessionChange();
  emitDemoModeChange();
}

export function clearPublicGuestSession() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(PUBLIC_GUEST_SESSION_STORAGE_KEY);
  } catch {}
  emitPublicGuestSessionChange();
  emitDemoModeChange();
}

function subscribePublicGuestSession(onStoreChange: () => void) {
  if (typeof window === 'undefined') return () => {};
  const handleCustom = () => onStoreChange();
  window.addEventListener(PUBLIC_GUEST_SESSION_CHANGED_EVENT, handleCustom as EventListener);
  window.addEventListener('focus', handleCustom);
  return () => {
    window.removeEventListener(PUBLIC_GUEST_SESSION_CHANGED_EVENT, handleCustom as EventListener);
    window.removeEventListener('focus', handleCustom);
  };
}

export function usePublicGuestSession(): boolean {
  return useSyncExternalStore(subscribePublicGuestSession, readPublicGuestSession, () => false);
}

export function useGuestPermissions(): { isGuest: boolean; permissions: GuestPermissions; appMode: AppMode } {
  const isGuest = usePublicGuestSession();
  return {
    isGuest,
    appMode: isGuest ? 'guest' : 'authenticated',
    permissions: isGuest ? GUEST_MODE_PERMISSIONS : AUTHENTICATED_PERMISSIONS,
  };
}

const GUEST_MODE_STATE: DemoModeState = {
  enabled: true,
  datasetId: DASHBOARD_DEMO_DATASET_ID,
  enabledAt: null,
};

export function readDemoModeState(): DemoModeState {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  if (readPublicGuestSession()) {
    return GUEST_MODE_STATE;
  }
  if (isDemoModeTemporarilyDisabled()) {
    return DEFAULT_STATE;
  }

  try {
    const raw = window.localStorage.getItem(DASHBOARD_DEMO_MODE_STORAGE_KEY);
    if (!raw) {
      cachedRawSnapshot = null;
      cachedSnapshot = DEFAULT_STATE;
      return DEFAULT_STATE;
    }

    if (raw === cachedRawSnapshot) {
      return cachedSnapshot;
    }

    cachedRawSnapshot = raw;
    cachedSnapshot = sanitizeDemoModeState(JSON.parse(raw));
    return cachedSnapshot;
  } catch {
    cachedRawSnapshot = null;
    cachedSnapshot = DEFAULT_STATE;
    return DEFAULT_STATE;
  }
}

function emitDemoModeChange() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(DASHBOARD_DEMO_MODE_CHANGED_EVENT));
}

export function enableDemoMode(datasetId = DASHBOARD_DEMO_DATASET_ID) {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(DEMO_MODE_TEMPORARILY_DISABLED_KEY);
  } catch {}

  try {
    window.localStorage.setItem(
      DASHBOARD_DEMO_MODE_STORAGE_KEY,
      JSON.stringify({
        enabled: true,
        datasetId,
        enabledAt: new Date().toISOString(),
      })
    );
  } catch {}

  emitDemoModeChange();
}

export function disableDemoMode() {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(DASHBOARD_DEMO_MODE_STORAGE_KEY);
  } catch {}

  emitDemoModeChange();
}

function subscribe(onStoreChange: () => void) {
  if (typeof window === 'undefined') return () => {};

  const handleCustom = () => onStoreChange();
  const handleStorage = (event: StorageEvent) => {
    if (!event.key || event.key === DASHBOARD_DEMO_MODE_STORAGE_KEY) {
      onStoreChange();
    }
  };

  window.addEventListener(DASHBOARD_DEMO_MODE_CHANGED_EVENT, handleCustom as EventListener);
  window.addEventListener('storage', handleStorage);
  window.addEventListener('focus', handleCustom);

  return () => {
    window.removeEventListener(DASHBOARD_DEMO_MODE_CHANGED_EVENT, handleCustom as EventListener);
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('focus', handleCustom);
  };
}

export function useDemoModeState() {
  return useSyncExternalStore(subscribe, readDemoModeState, () => DEFAULT_STATE);
}

export function DemoTradesOverrideProvider({
  trades,
  children,
}: {
  trades?: Record<string, any>[] | null;
  children: ReactNode;
}) {
  const value = useMemo<DemoTradesOverride>(() => {
    if (!Array.isArray(trades) || trades.length === 0) return null;
    return { enabled: true, trades };
  }, [trades]);

  return (
    <DemoTradesOverrideContext.Provider value={value}>
      {children}
    </DemoTradesOverrideContext.Provider>
  );
}

export function useDemoTradesOverride() {
  return useContext(DemoTradesOverrideContext);
}

export function shouldUseDemoTrades({
  isDemoMode,
  accountType,
  hasRealTrades,
}: {
  isDemoMode: boolean;
  accountType?: string | null;
  hasRealTrades: boolean;
}): boolean {
  if (isDemoMode) {
    return true;
  }
  if (hasRealTrades) {
    return false;
  }
  return accountType === 'demo';
}

export const DEMO_MODE_TEMPORARILY_DISABLED_KEY = 'tradeproof.demo-mode-temporarily-disabled.v1';

export function isDemoModeTemporarilyDisabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(DEMO_MODE_TEMPORARILY_DISABLED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setDemoModeTemporarilyDisabled(val: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (val) {
      window.sessionStorage.setItem(DEMO_MODE_TEMPORARILY_DISABLED_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(DEMO_MODE_TEMPORARILY_DISABLED_KEY);
    }
  } catch {}
  emitDemoModeChange();
}

